<template>
  <div class="Container" v-if="notifications.length > 0">
    <div :class="toastClasses" @click="dismiss" @mouseover="pause" @mouseleave="unpause">
      <div :class="{ 'Toast-Body': true, Mobile: $mq !== 'desktop' }">
        <img v-if="image" :src="image" :alt="title" />
        <div class="Toast-Text-Wrapper">
          <div class="Toast-Title">{{ title }}</div>
          <div class="Toast-Message">{{ message }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import get from 'lodash/get'

import { IN_BETWEEN_NOTIFICATION_DELAY } from '@/services/notification'
import {
  NOTIFICATION_SEEN,
  NOTIFICATION_TOASTED,
  SET_NOTIFICATIONS
} from '@/store/modules/notification/types'

export default {
  name: 'NotificationToaster',
  data() {
    return {
      active: false,
      timerId: null
    }
  },
  mounted() {
    this.retrieveNotifications()
  },
  computed: {
    ...mapGetters({
      notifications: 'notificationsToToast'
    }),
    notification() {
      return this.notifications[0] || null
    },
    title() {
      return get(this.notification, ['toast', 'data', 'title'], null)
    },
    message() {
      return get(this.notification, ['toast', 'data', 'message'], null)
    },
    image() {
      return get(this.notification, ['toast', 'data', 'image'], null)
    },
    toastClasses() {
      const classes = ['Toast']

      if (this.active) {
        classes.push('Visible')
      }

      return classes
    }
  },
  methods: {
    dismiss() {
      const cancellable = get(this.notification, ['toast', 'cancellable'], true)

      if (!cancellable) return

      this.completeCurrentNotification(true)
    },
    completeCurrentNotification(fromUser = false) {
      this.active = false

      if (this.timerId) {
        clearTimeout(this.timerId)
      }

      const notificationId = this.notification.id
      // Set the notification as toasted
      this.$store.dispatch(NOTIFICATION_TOASTED, notificationId)

      // If the user dismissed it, consider it as seen/acknowledge by him
      if (fromUser) {
        this.$store.dispatch(NOTIFICATION_SEEN, notificationId)
      }

      // Delay a bit the next notification
      setTimeout(() => {
        if (this.notification) {
          this.active = true

          // Restart the loop with the new notification
          this.timerId = setTimeout(
            this.completeCurrentNotification,
            this.notification.toast.duration
          )
        }
      }, IN_BETWEEN_NOTIFICATION_DELAY)
    },
    pause() {
      if (this.active && this.timerId) {
        clearTimeout(this.timerId)
      }
    },
    unpause() {
      if (!this.active) return

      setTimeout(() => {
        if (this.notification) {
          this.active = true

          // Restart the loop with the new notification
          this.timerId = setTimeout(
            this.completeCurrentNotification,
            this.notification.toast.duration
          )
        }
      }, IN_BETWEEN_NOTIFICATION_DELAY)
    },
    /**
     * Rehydrate notification from the store on component mounted
     */
    async retrieveNotifications() {
      const notifications = await this.$firestore.notifications.getAll()

      if (notifications && notifications.length > 0) {
        this.$store.commit(SET_NOTIFICATIONS, notifications)
      }
    }
  },
  watch: {
    notification(currentNotification, oldNotification) {
      /**
       * Only activate the notification loop the first time the notification is defined
       * Otherwise, the [completeCurrentNotification] will handle the loop
       */
      if (!currentNotification || (currentNotification && oldNotification)) return

      this.active = true

      this.timerId = setTimeout(
        this.completeCurrentNotification,
        currentNotification.toast.duration
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.Container {
  position: fixed;
  bottom: 8px;
  left: 0;
  right: 0;
  text-align: center;
  user-select: none;
  overflow: hidden;
  height: auto;
  transition-duration: 0ms;
  transition-delay: 400ms;
  transition-property: all;
  transform: translateY(0);

  &.Hidden {
    transform: translateY(200px);
  }

  .Toast {
    cursor: pointer;
    display: inline-block;
    background-color: rgba(0, 0, 0, 0.7);
    border-radius: 6px;
    padding: 15px;
    color: white;
    transform: translateY(70px);
    opacity: 0;
    transition: all 200ms linear;

    &.Visible {
      opacity: 1;
      transform: translateY(0);
    }

    .Toast-Body {
      height: 50px;
      min-width: 30vw;
      text-align: left;
      &.Mobile {
        min-width: 90vw;
      }

      img {
        height: 50px;
        max-width: 50px;
        margin-right: 12px;
        border-radius: 4px;
      }

      .Toast-Text-Wrapper {
        vertical-align: top;
        display: inline-block;

        .Toast-Title {
          font-size: 16px;
          font-weight: bold;
          margin-bottom: 6px;
        }

        .Toast-Message {
          font-size: 12px;
        }
      }
    }
  }
}
</style>
